/**
 * §7 FAQ —— cjwf-premium-faq（9 条手风琴 + 左侧常驻说明）
 *
 * 需求来源：提示词 第1次提交（DEV-124229）；原型 L10787-11606 + 内联 <script> L11609。
 *
 * 注意事项：
 * 1. 【为什么用 React state 而不是照搬 classList】原型把默认展开态**烤进 markup**
 *    （Q01 带 class="cjwf-premium-item active"），再用原生 JS 增删 .active。
 *    SSR 直出的 HTML 与客户端 state 必须一致，否则 hydration 告警；且原型那段 IIFE 用全局
 *    document.querySelectorAll 绑定，二次挂载会重复绑（一次点击切两次）。
 *    故改为 useState(0)：默认展开第 1 条，与原型初始视觉一致。
 *    同款结论见 app/web/page/alternatives/index.jsx:176-178。
 * 2. 手风琴为**单开**（展开一条会收起其它），与原型逻辑一致。
 * 3. 展开动画依赖 CSS 的 grid-template-rows: 0fr → 1fr（Chrome 107+/Safari 16+/Firefox 120+），
 *    原样保留；旧版 Firefox 会瞬间展开而非过渡（不影响可用性）。
 * 4. 答案体结构较杂（普通段落 / 任务卡 / 支付方式 / 无序列表 / 有序步骤 / 国家表 / 安全标记），
 *    按 constants.js 里各条目提供的字段**按需渲染**，缺字段即不渲染对应块。
 */
import React, { useState, useCallback } from 'react';
import EmphasisText from './EmphasisText';

export default function Faq({ data }) {
  // 与原型一致：默认展开第 1 条（原型是在 markup 上烤了 .active）
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = useCallback((i) => {
    setOpenIndex((cur) => (cur === i ? -1 : i));
  }, []);

  if (!data) return null;
  const items = data.items || [];

  /*
   * 段落正文的强调词已内嵌在词条里（`<strong>…</strong>`，见 index.less 末尾「配色承接」段），
   * 故直接把 HTML 挂到 <p> 上，不再经 EmphasisText 拼装。
   * pClass 是**不参与翻译**的样式钩子：全页唯一一处粉色强调（Q01 第 2 段）靠它区分 ——
   * 同一个 .cjwf-premium-answer 里其它段落的 <strong> 是默认深色，容器选择器分不开。
   */
  const renderParas = (paras) =>
    (paras || []).map((p, i) => (
      <p key={i} className={p.pClass}>
        <EmphasisText text={p.text} />
      </p>
    ));

  return (
    <section className="cjwf-premium-faq">
      <div className="cjwf-premium-container">
        <div className="cjwf-premium-header">
          <h2 className="cjwf-premium-title">
            <EmphasisText text={data.title} />
          </h2>
          <p className="cjwf-premium-desc">{data.desc}</p>
        </div>

        <div className="cjwf-premium-layout">
          <aside className="cjwf-premium-side">
            {/* 【这个无类名 <div> 不能省】.cjwf-premium-side 是 `display:grid; gap:25px`
                （PC 为 `1fr auto` 两列，手机端塌成 1 列）。原型把 label+title+text 包成**一个**格子，
                于是整块只有 2 个格子、gap 只算 1 次。
                我最初把三者平铺成直接子元素 → 4 个格子、gap 算 3 次，侧栏凭空高出 50px
                （375px 下实测 375 vs 327）。 */}
            <div>
              <div className="cjwf-premium-side-label">{data.sideLabel}</div>
              <h3 className="cjwf-premium-side-title">
                <EmphasisText text={data.sideTitle} />
              </h3>
              <p className="cjwf-premium-side-text">{data.sideText}</p>
            </div>
            <div className="cjwf-premium-side-stat">
              <span>{data.sideStat}</span>
            </div>
          </aside>

          <div className="cjwf-premium-list">
            {items.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div className={'cjwf-premium-item' + (isOpen ? ' active' : '')} key={i}>
                  <button
                    type="button"
                    className="cjwf-premium-question"
                    onClick={() => toggle(i)}
                    aria-expanded={isOpen}
                  >
                    <span className="cjwf-premium-q-content">
                      <span className="cjwf-premium-number">{item.num}</span>
                      <span className="cjwf-premium-question-text">{item.q}</span>
                    </span>
                    <span className="cjwf-premium-icon" />
                  </button>

                  {/* 答案始终留在 DOM 里（靠 CSS 的 0fr→1fr 收起），这样爬虫能读到全部问答；
                      单测 CASE-R3 断言 9 条问题文本都出现在 SSR 输出中 */}
                  <div className="cjwf-premium-answer-wrap">
                    <div className="cjwf-premium-answer-inner">
                      <div className="cjwf-premium-answer">
                        {renderParas(item.paras)}

                        {item.promoLabel && (
                          <div className="cjwf-premium-promo">
                            <div className="cjwf-premium-promo-label">{item.promoLabel}</div>
                            <div className="cjwf-premium-task-grid">
                              {(item.tasks || []).map((t, j) => (
                                <div className="cjwf-premium-task" key={j}>
                                  <div className="cjwf-premium-task-number">{t.num}</div>
                                  <strong>{t.title}</strong>
                                  <span>{t.text}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {item.list && (
                          <ul>
                            {item.list.map((li, j) => (
                              <li key={j}>
                                <EmphasisText text={li.text} />
                              </li>
                            ))}
                          </ul>
                        )}

                        {item.ordered && (
                          <ol>
                            {item.ordered.map((li, j) => (
                              <li key={j}>
                                <EmphasisText text={li.text} />
                              </li>
                            ))}
                          </ol>
                        )}

                        {item.countries && (
                          <div className="cjwf-premium-country-grid">
                            {item.countries.map((c, j) => (
                              <div className="cjwf-premium-country" key={j}>
                                <span>{c}</span>
                                <span>{item.daysText}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* 【顺序按原型】parasAfter 必须排在 methods **之前**：
                            Q02 的结构是 paras → promo(tasks) → "The US$100 bonus will be credited…" → methods。
                            我最初把 parasAfter 放在 methods 之后，那句话就跑到三种支付方式下面去了。 */}
                        {!item.security && renderParas(item.parasAfter)}

                        {item.security && (
                          /* 【Q09 安全块】原型把**两段正文都包在 .cjwf-premium-security 内部**的一个 <div> 里，
                             与左侧图标构成 flex 两列；图标是**字面 ✓ 字符**（.cjwf-premium-security-icon 是
                             44x44 粉色圆角块），不是 SVG —— 我最初用了 IconCheck 组件且把第二段留在块外。 */
                          <div className="cjwf-premium-security">
                            <div className="cjwf-premium-security-icon">✓</div>
                            <div>
                              <p><strong>{item.security}</strong></p>
                              {(item.parasAfter || []).map((pa, j) => (
                                <p key={j}>
                                  <EmphasisText text={pa.text} />
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        {item.methods && (
                          <div className="cjwf-premium-methods">
                            {item.methods.map((m, j) => (
                              <div className="cjwf-premium-method" key={j}>
                                <div className="cjwf-premium-method-icon">{m.icon}</div>
                                <div className="cjwf-premium-method-title">{m.title}</div>
                                <p>{m.text}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
